import { IEnvironmentConfiguration } from '@ups/xplat/core';

/**
 * The whole of `environment.rynReplay`, not a hand-copied subset.
 *
 * Declaring only the three fields this file reads is tempting and wrong: every
 * real caller passes the entire config object, so a narrowed type rejects a
 * config literal for naming a field that genuinely exists, and the error names
 * the field rather than the narrowing ("'enabled' does not exist in type
 * 'RynReplayConfig'"), which reads as the interface being wrong.
 */
export type RynReplayConfig = IEnvironmentConfiguration['rynReplay'];

/**
 * Default CSS class an app puts on any element whose subtree must never be
 * captured. The escape hatch for regions masking alone cannot cover (embedded
 * documents, customer-supplied images, third-party iframes).
 */
export const RYN_REPLAY_DEFAULT_BLOCK_CLASS = 'ryn-replay-block';

/** Masks an element's text but keeps its layout (WI 34326); pinned like the block class. */
export const RYN_REPLAY_DEFAULT_MASK_TEXT_CLASS = 'ryn-replay-mask';

/**
 * Baseline CSS selectors the recorder always blocks outright, regardless of
 * config (PR 3639 review):
 * - `kendo-numerictextbox` mirrors its bound value onto the host's
 *   `aria-valuenow`, which neither `maskTextSelector` nor `maskAllInputs`
 *   reaches, so a pay-rate control could leak a figure with full-text
 *   masking on.
 * - `kendo-calendar` is the content of every Kendo date picker popup (and a
 *   standalone calendar). Its cells carry the full date in `aria-label` /
 *   `title`, so an expiry or birth date picker leaks the selected date. The
 *   popup is appended to the body, outside any `ryn-replay-block` ancestor,
 *   so a selector covers every picker without a per-template `popupClass`.
 * - `kendo-datepicker` and `kendo-dateinput`: while a picker is open, Kendo
 *   (dateinputs 22.0.1) copies the focused calendar cell id onto the
 *   picker's own `<input>` as `aria-activedescendant`, and that id embeds
 *   the date as epoch ms (`${cellUID}${cellDate.getTime()}`).
 *   `maskAllInputs` only masks the input's value, so the attribute would
 *   record a DOB or expiry date (PR 3639 review, thread 55971).
 */
export const RYN_REPLAY_BASELINE_BLOCK_SELECTORS: readonly string[] = [
  'kendo-numerictextbox',
  'kendo-calendar',
  'kendo-datepicker',
  'kendo-dateinput',
];

export interface IRynReplayMaskingPolicy {
  /** When true every input value is redacted before it leaves the page. */
  maskAllInputs: boolean;
  /**
   * When true every text node on the page is masked, not just typed input
   * values. Off by default (PR 3639 review): `maskAllInputs` alone never
   * covered rendered text (export-error strings, PII rendered outside a form
   * control), and rrweb ships with no maskText* option set at all. Per-app
   * opt-in only, so turning it on does not change every app's recording
   * behavior at once.
   */
  maskAllText: boolean;
  /** Text-mask class for any app. Always the pinned default (WI 34326). */
  maskTextClass: string;
  /**
   * CSS class marking a subtree as never-record. Always
   * `RYN_REPLAY_DEFAULT_BLOCK_CLASS`; config cannot change it (PR 3639
   * review, see `buildRynReplayMaskingPolicy`).
   */
  blockClass: string;
  /**
   * CSS selector(s) blocked outright regardless of `blockClass`. Always
   * includes `RYN_REPLAY_BASELINE_BLOCK_SELECTORS`; config may only append to
   * it, never replace or drop it, and only takes effect once `maskAllText`
   * has opted the app into the stricter policy.
   */
  blockSelector: string;
  /**
   * Allowlist of URLs whose request/response payloads may be recorded.
   * Empty means nothing may be recorded, so payload capture is opt-in.
   */
  allowedUrlsForPayloadRecord: RegExp[];
  /**
   * Hosts a payload may come from, checked on the PARSED URL host before any
   * URL pattern runs. `*.example.com` matches any subdomain of example.com (not
   * the apex); anything else is an exact host.
   */
  allowedHostsForPayloadRecord: string[];
}

/**
 * Hosts UPS owns and whose API responses may be body-recorded (WI 34576).
 *
 * UPS subdomains are a dot-suffix match. Azure Web App names are claimable by
 * any tenant, so `azurewebsites.net` is never a suffix here: each owned app is
 * listed exactly, taken from the Urls:* values in Azure App Configuration. A new
 * API is added here or through RynReplay:AllowedHostsForPayloadRecord; until it
 * is, its bodies are simply not recorded, which is the safe failure. The replay
 * service's own hosts are left out on purpose, the recorder never records itself.
 */
export const RYN_REPLAY_OWNED_PAYLOAD_HOSTS: readonly string[] = [
  '*.universalplant.com',
  'azure-api-upsllc.azurewebsites.net',
  'stg-azure-api-upsllc.azurewebsites.net',
  'dynamic-content-api-upsllc.azurewebsites.net',
  'stg-dynamic-content-api-upsllc.azurewebsites.net',
  'employee-packet-api-upsllc.azurewebsites.net',
  'stg-employee-packet-api-upsllc.azurewebsites.net',
  'rates-management-api-upsllc.azurewebsites.net',
  'stg-rates-management-api-upsllc.azurewebsites.net',
  'stg-security-api-upsllc.azurewebsites.net',
  'tasks-api-upsllc.azurewebsites.net',
  'stg-tasks-api-upsllc.azurewebsites.net',
  'tool-tracker-api-upsllc.azurewebsites.net',
  'stg-tool-tracker-api-upsllc.azurewebsites.net',
  'fn-ryn-bridge-upsllc.azurewebsites.net',
  'prod-fn-project-hub-upsllc.azurewebsites.net',
  'stg-fn-project-hub-upsllc.azurewebsites.net',
  // STG web apps: their relative same-origin calls resolve to these hosts.
  'stg-my-web-upsllc.azurewebsites.net',
  'stg-sparta-web-upsllc.azurewebsites.net',
  'stg-safety-web-upsllc.azurewebsites.net',
  'stg-resource-allocation-web-upsllc.azurewebsites.net',
  'stg-warehouse-management-web-upsllc.azurewebsites.net',
  'stg-enterprise-appstore-upsllc.azurewebsites.net',
];

/**
 * Compile the configured allowlist.
 *
 * `allowedUrlsForPayloadRecord` is a single regex source string (same shape
 * the retired SmartLook env key used). A
 * comma separated list is additionally supported for apps needing more than one
 * pattern. Invalid or blank patterns are dropped rather than thrown, because
 * this runs during app bootstrap and an empty allowlist fails closed.
 */
const compileAllowedUrls = (value?: string): RegExp[] => {
  if (!value) {
    return [];
  }
  return value
    .split(',')
    .map((pattern) => pattern.trim())
    .filter((pattern) => pattern.length > 0)
    .reduce<RegExp[]>((patterns, pattern) => {
      try {
        patterns.push(new RegExp(pattern));
      } catch {
        // A typo in an Azure App Configuration value must not break bootstrap.
      }
      return patterns;
    }, []);
};

/**
 * Parse the configured host list. Unset or blank keeps the owned default, so a
 * missing App Config value can never lift the host restriction.
 */
const parseAllowedHosts = (value?: string): string[] => {
  const hosts = (value ?? '')
    .split(',')
    .map((host) => host.trim().toLowerCase())
    .filter((host) => host.length > 0);
  return hosts.length > 0 ? hosts : [...RYN_REPLAY_OWNED_PAYLOAD_HOSTS];
};

const hostMatches = (host: string, entry: string): boolean => {
  if (entry.startsWith('*.')) {
    return host.endsWith(entry.slice(1));
  }
  return host === entry;
};

/**
 * Build the blocked-selector list: the baseline always first, any config
 * extras appended, deduped. Config has no path to remove or replace the
 * baseline, only to add to it (PR 3639 review, thread 55948).
 */
const buildBlockSelector = (extra?: string): string => {
  const extras = (extra ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(
      (s) => s.length > 0 && !RYN_REPLAY_BASELINE_BLOCK_SELECTORS.includes(s)
    );
  return [...RYN_REPLAY_BASELINE_BLOCK_SELECTORS, ...extras].join(', ');
};

export const buildRynReplayMaskingPolicy = (
  config?: RynReplayConfig
): IRynReplayMaskingPolicy => ({
  // Only an explicit `false` may turn masking off. An unresolved config key
  // arrives as undefined and must keep redaction on.
  maskAllInputs: config?.maskAllInputs !== false,
  // Opposite default polarity from maskAllInputs on purpose: only an explicit
  // `true` may turn full-text masking ON, since it changes recording
  // behavior for every page in the app, not just forms.
  maskAllText: config?.maskAllText === true,
  // Pinned (PR 3639 review, thread 55945/55948): every template in the repo
  // hard-codes the literal `ryn-replay-block` class, so config is never
  // consulted here, not even to "override to the same value". A config key
  // that cannot do anything is safer than one that can weaken blocking by
  // typo or a well-meaning app override.
  blockClass: RYN_REPLAY_DEFAULT_BLOCK_CLASS,
  maskTextClass: RYN_REPLAY_DEFAULT_MASK_TEXT_CLASS,
  blockSelector: buildBlockSelector(config?.extraBlockSelector),
  allowedUrlsForPayloadRecord: compileAllowedUrls(
    config?.allowedUrlsForPayloadRecord
  ),
  allowedHostsForPayloadRecord: parseAllowedHosts(
    config?.allowedHostsForPayloadRecord
  ),
});

// SSN masking lives in ryn-replay-ssn-mask.ts (bug 35675); re-exported here so existing imports keep working.
export {
  RYN_REPLAY_MASKED_SSN,
  maskSsnLikeInText,
} from './ryn-replay-ssn-mask';

/**
 * Enforcement point for the payload allowlist. The network capture layer calls
 * this before retaining any request or response body.
 *
 * The host is decided on the PARSED URL, never by a pattern over the raw string
 * (WI 34576 review): a raw-string match admits `universalplant.com.evil.io`,
 * `evil.example/?next=https://api.universalplant.com` and every tenant's
 * `*.azurewebsites.net`. Only an https URL on an allowed host reaches the URL
 * patterns, which then narrow by path. A relative URL resolves against the page.
 */
export const isPayloadRecordingAllowedForUrl = (
  policy: IRynReplayMaskingPolicy,
  url: string,
  pageUrl: string | undefined = globalThis.location?.href
): boolean => {
  if (!url) {
    return false;
  }
  let parsed: URL;
  try {
    parsed = new URL(url, pageUrl);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') {
    return false;
  }
  const host = parsed.hostname.toLowerCase().replace(/\.$/, '');
  if (!policy.allowedHostsForPayloadRecord.some((e) => hostMatches(host, e))) {
    return false;
  }
  const absolute = parsed.toString();
  return policy.allowedUrlsForPayloadRecord.some((pattern) =>
    pattern.test(absolute)
  );
};
